import { useCallback, useEffect, useState } from "react";
import { catalogApi, type JournalHoldingsTree } from "@/lib/api/catalog";
import type { JournalBiblio } from "./journal-details.types";

interface JournalDetailsState {
  biblio: JournalBiblio | null;
  holdings: JournalHoldingsTree | null;
  loading: boolean;
  error: string | null;
}

const INITIAL_STATE: JournalDetailsState = {
  biblio: null,
  holdings: null,
  loading: true,
  error: null,
};

function getErrorMessage(error: unknown) {
  return error instanceof Error && error.message
    ? error.message
    : "Unable to load journal details. Please try again.";
}

export function useJournalDetails(id?: string) {
  const [state, setState] = useState<JournalDetailsState>(INITIAL_STATE);

  const load = useCallback(async () => {
    if (!id) {
      setState({ ...INITIAL_STATE, loading: false, error: "Missing journal ID." });
      return;
    }
    setState((current) => ({ ...current, loading: true, error: null }));
    try {
      const [biblio, holdings] = await Promise.all([
        catalogApi.getBiblio(id) as Promise<JournalBiblio>,
        catalogApi.getJournalHoldings(id),
      ]);
      setState({ biblio, holdings, loading: false, error: null });
    } catch (error) {
      setState({ ...INITIAL_STATE, loading: false, error: getErrorMessage(error) });
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const refreshHoldings = useCallback(async () => {
    if (!id) return;
    const holdings = await catalogApi.getJournalHoldings(id);
    setState((current) => ({ ...current, holdings }));
  }, [id]);

  return { ...state, refreshHoldings };
}
